import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Plus, Users, Edit, Trash2, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

function Batches() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isFaculty = user.role === 'faculty';
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentBatch, setCurrentBatch] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    year: new Date().getFullYear().toString()
  });

  useEffect(() => {
    fetchBatches();
  }, []);

  const fetchBatches = async () => {
    try {
      const response = await api.get('/batches');
      setBatches(response.data);
    } catch (error) {
      console.error('Error fetching batches:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode && currentBatch) {
        await api.put(`/batches/${currentBatch.id}`, formData);
        toast.success('Batch updated successfully');
      } else {
        await api.post('/batches', formData);
        toast.success('Batch created successfully');
      }
      setModalOpen(false);
      resetForm();
      fetchBatches();
    } catch (error) {
      console.error('Error saving batch:', error);
      toast.error(error.response?.data?.detail || 'Failed to save batch');
    }
  };

  const handleEdit = (batch) => {
    setCurrentBatch(batch);
    setFormData({
      name: batch.name,
      description: batch.description || '',
      year: batch.year || new Date().getFullYear().toString()
    });
    setEditMode(true);
    setModalOpen(true);
  };

  const handleDelete = async (batchId) => {
    if (!window.confirm('Are you sure you want to delete this batch?')) return;
    try {
      await api.delete(`/batches/${batchId}`);
      toast.success('Batch deleted successfully');
      fetchBatches();
    } catch (error) {
      console.error('Error deleting batch:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete batch');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', description: '', year: new Date().getFullYear().toString() });
    setCurrentBatch(null);
    setEditMode(false);
  };

  const viewBatchResults = (batchId) => {
    navigate(`/admin/rankings`, { state: { batchId } });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="spinner"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Batch Management</h1>
            <p className="text-gray-600">Organize students into batches for easy management</p>
          </div>
          {!isFaculty && (
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              onClick={() => {
                resetForm();
                setModalOpen(true);
              }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Batch
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {batches.map((batch) => (
            <Card key={batch.id} className="p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 mb-1">{batch.name}</h3>
                  {batch.year && (
                    <div className="flex items-center text-sm text-gray-600 mb-2">
                      <Calendar className="w-4 h-4 mr-1" />
                      {batch.year}
                    </div>
                  )}
                  {batch.description && (
                    <p className="text-sm text-gray-600 mb-3">{batch.description}</p>
                  )}
                </div>
                <div className="flex gap-2">
                  {!isFaculty && (
                  <>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleEdit(batch)}
                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(batch.id)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                  </>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between pt-4 border-t">
                <div className="flex items-center text-gray-700">
                  <Users className="w-5 h-5 mr-2" />
                  <span className="font-medium">{batch.user_count || 0} Students</span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => viewBatchResults(batch.id)}
                >
                  View Results
                </Button>
              </div>
            </Card>
          ))}
        </div>

        {batches.length === 0 && (
          <div className="text-center py-12">
            <h3 className="text-lg font-medium text-gray-900 mb-2">No batches yet</h3>
            <p className="text-gray-500 mb-4">Create your first batch to organize students</p>
          </div>
        )}
      </div>

      <Dialog open={modalOpen} onOpenChange={(open) => {
        if (!open) resetForm();
        setModalOpen(open);
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editMode ? 'Edit Batch' : 'Create New Batch'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Batch Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Batch 2024-A"
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Year</Label>
              <Input
                value={formData.year}
                onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                placeholder="e.g., 2024"
              />
            </div>

            <div className="space-y-2">
              <Label>Description (Optional)</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Add details about this batch"
                rows={3}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                {editMode ? 'Update' : 'Create'} Batch
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

export default Batches;
